import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BiContextProvidedValue,
  BiData,
  CommentButtonClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { useEditorDraftActions } from '@wix/da-editor-tiptap/pkg/clientDrafts/hooks';
import { publishComment } from '../redux/actionCreators';
import { useVerification } from '../../VerificationPopup/withVerification';
import { CommentEditorContext } from '../context';
import { useIsCommentEditorSubmittable } from './useIsCommentEditorSubmittable';
import { CommentEditorSource } from '../redux/types';

/**
 * Provides a function that will post a comment!
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */
export function useCommentPostAction({
  messageId,
  folderId,
  shouldDeleteMessage,
  onPost,
}: {
  messageId?: string;
  folderId?: number | null;
  shouldDeleteMessage?: boolean;
  onPost?: () => void;
} = {}) {
  const {
    isBusy,
    editorId,
    commentableType,
    commentableItemId,
    editorSource,
    relevantComment,
  } = useContext(CommentEditorContext);
  const {
    ref: editor,
    isReady: isEditorReady,
    getStateForSave: getEditorStateForSave,
  } = useContext(EditorContext);
  const dispatch = useDispatch();
  const { pushVerificationPopup, isVerificationNeeded } = useVerification();
  const { removeEditorDraft } = useEditorDraftActions(editorId);
  const isSubmittable = useIsCommentEditorSubmittable();
  const logBiEvent = useBiLogger();

  const lastExclusiveRaw = useSelector(state =>
    getCompliantLocalStorageItem(
      state as any,
      CompliantLocalStorageKeys.EXCLUSIVE_AVATAR_LAST_SELECTED
    )
  );

  const postComment = useCallback(async () => {
    if (isBusy || !isSubmittable || !isEditorReady) {
      return;
    }

    if (isVerificationNeeded) {
      pushVerificationPopup('comment');
      return;
    }

    // Clean up draft state in local storage
    removeEditorDraft();

    // If a last-selected exclusive avatar is present, ensure the editor contains exactly one
    // da-exclusive-avatar node with that selection (clean old nodes first, then insert)
    try {
      const parsed = lastExclusiveRaw ? JSON.parse(lastExclusiveRaw) : null;

      if (parsed && (parsed as any).deviationId && editor && isEditorReady) {
        const { state, view } = editor as any;
        const tr = state.tr;
        const ranges: Array<{ from: number; to: number }> = [];

        state.doc.descendants((node: any, pos: number) => {
          if (node?.type?.name === 'da-exclusive-avatar') {
            ranges.push({ from: pos, to: pos + node.nodeSize });
          }
        });

        ranges
          .sort((a, b) => b.from - a.from)
          .forEach(({ from, to }) => tr.delete(from, to));

        if (tr.docChanged) {
          view.dispatch(tr);
        }

        try {
          editor.commands.insertContent({
            type: 'da-exclusive-avatar',
            attrs: { deviation: parsed },
          } as any);
        } catch (e) {}
      }
    } catch (e) {}

    if (editorSource !== CommentEditorSource.Edit) {
      logBiEvent(
        BiData<CommentButtonClickBiEvent>({
          evid: 184,
          itemid:
            typeof commentableItemId === 'string'
              ? parseInt(commentableItemId)
              : commentableItemId,
          typeid: commentableType,
          notification_type: BiContextProvidedValue,
          is_comment_thread: BiContextProvidedValue,
          is_reply: editorSource === CommentEditorSource.Reply ? 1 : 0,
          is_sticky: 0,
        })
      );
    }
    dispatch(
      publishComment({
        editorId,
        typeId: commentableType,
        itemId: commentableItemId,
        rawContentState: JSON.stringify(getEditorStateForSave()),
        editComment:
          editorSource === CommentEditorSource.Edit
            ? relevantComment
            : undefined,
        replyComment:
          editorSource === CommentEditorSource.Reply
            ? relevantComment
            : undefined,
        messageId,
        folderId,
        shouldDeleteMessage,
      })
    );

    onPost && onPost();
  }, [
    dispatch,
    isSubmittable,
    isEditorReady,
    getEditorStateForSave,
    isBusy,
    editor,
    editorId,
    editorSource,
    relevantComment,
    commentableType,
    commentableItemId,
    pushVerificationPopup,
    isVerificationNeeded,
    removeEditorDraft,
    logBiEvent,
    messageId,
    shouldDeleteMessage,
    folderId,
    onPost,
    lastExclusiveRaw,
  ]);
  return postComment;
}
